import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "./ui/select";
import { type TableTheme } from "../types";
import { useState, useEffect } from "react";
import { RotateCcw } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from "./ui/tooltip";
import {
  startOfDay,
  endOfDay,
  subDays,
  subMonths,
  subYears,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  startOfYear,
  endOfYear,
} from "date-fns";

interface DateRangeFilterProps {
  onDateRangeChange: (
    range: string,
    startDate?: string,
    endDate?: string
  ) => void;
  currentRange: string;
  theme: TableTheme;
  serverSide?: boolean;
  onServerDateRangeChange?: (
    field: string,
    range: string,
    startDate?: string,
    endDate?: string
  ) => void;
  dateField?: string;
  enableCustomDateRangeFilter: boolean;
}

const toUTCISOString = (d: Date) =>
  new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString();

const calculateDateRange = (
  range: string
): { startDate: string; endDate: string } | null => {
  if (range === "all" || range === "custom") return null;

  const now = new Date();
  const today = startOfDay(now);

  let startDate: Date;
  let endDate: Date;

  switch (range) {
    case "today":
      startDate = startOfDay(today);
      endDate = endOfDay(today);
      break;

    case "yesterday":
      startDate = startOfDay(subDays(today, 1));
      endDate = endOfDay(subDays(today, 1));
      break;

    case "tomorrow":
      startDate = startOfDay(subDays(today, -1));
      endDate = endOfDay(subDays(today, -1));
      break;

    case "last_3_days":
      startDate = startOfDay(subDays(today, 3));
      endDate = endOfDay(subDays(today, 1));
      break;

    case "this_week": {
      startDate = startOfWeek(today, { weekStartsOn: 1 }); // Monday
      endDate = endOfWeek(today, { weekStartsOn: 1 }); // Sunday
      break;
    }

    case "last_week": {
      const lastWeek = subDays(today, 7);
      startDate = startOfWeek(lastWeek, { weekStartsOn: 1 });
      endDate = endOfWeek(lastWeek, { weekStartsOn: 1 });
      break;
    }
    case "last_15_days":
      startDate = startOfDay(subDays(today, 15));
      endDate = endOfDay(subDays(today, 1));
      break;

    case "this_month": {
      startDate = startOfMonth(today);
      endDate = endOfMonth(today);
      break;
    }

    case "last_month":
      const lastMonthDate = subMonths(today, 1);
      startDate = startOfMonth(lastMonthDate);
      endDate = endOfMonth(lastMonthDate);
      break;

    case "last_6_months":
      const firstMonth = subMonths(startOfMonth(today), 6);
      const lastMonth = subMonths(startOfMonth(today), 1);
      startDate = startOfMonth(firstMonth);
      endDate = endOfMonth(lastMonth);
      break;

    case "this_year": {
      startDate = startOfYear(today);
      endDate = endOfYear(today);
      break;
    }

    case "last_year":
      const lastYearDate = subYears(today, 1);
      startDate = new Date(lastYearDate.getFullYear(), 0, 1); // Jan 1
      endDate = new Date(lastYearDate.getFullYear(), 11, 31, 23, 59, 59, 999); // Dec 31
      break;

    case "current_financial_year": {
      const year =
        today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
      startDate = new Date(year, 3, 1); // April 1
      endDate = new Date(year + 1, 2, 31, 23, 59, 59, 999); // March 31 next year
      break;
    }

    case "q1": {
      const year =
        today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
      startDate = new Date(year, 3, 1); // April
      endDate = new Date(year, 5, 30, 23, 59, 59, 999); // June
      break;
    }

    case "q2": {
      const year =
        today.getMonth() >= 6 ? today.getFullYear() : today.getFullYear() - 1;
      startDate = new Date(year, 6, 1); // July
      endDate = new Date(year, 8, 30, 23, 59, 59, 999); // September
      break;
    }

    case "q3": {
      const year =
        today.getMonth() >= 9 ? today.getFullYear() : today.getFullYear() - 1;
      startDate = new Date(year, 9, 1); // October
      endDate = new Date(year, 11, 31, 23, 59, 59, 999); // December
      break;
    }

    case "q4": {
      const year =
        today.getMonth() < 3 ? today.getFullYear() : today.getFullYear() + 1;
      startDate = new Date(year - 1, 0, 1); // January
      endDate = new Date(year - 1, 2, 31, 23, 59, 59, 999); // March
      break;
    }

    default:
      return null;
  }

  console.log(
    "range selected:",
    range,
    "today date:",
    toUTCISOString(today),
    "start date:",
    toUTCISOString(startDate),
    "end date:",
    toUTCISOString(endDate)
  );

  return {
    startDate: toUTCISOString(startDate),
    endDate: toUTCISOString(endDate),
  };
};

export const DateRangeFilter = ({
  onDateRangeChange,
  currentRange,
  theme,
  serverSide = false,
  onServerDateRangeChange,
  dateField = "createdAt", // Default field
  enableCustomDateRangeFilter = false,
}: DateRangeFilterProps) => {
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [isManualCustomChange, setIsManualCustomChange] = useState(false);

  const dateRangeOptions = [
    { label: "All Time", value: "all" },
    { label: "Yesterday", value: "yesterday" },
    { label: "Today", value: "today" },
    { label: "Tomorrow", value: "tomorrow" },
    { label: "Last 3 Days", value: "last_3_days" },
    { label: "Current Week", value: "this_week" },
    { label: "Previous Week", value: "last_week" },
    { label: "Previous 15 Days", value: "last_15_days" },
    { label: "Current Month", value: "this_month" },
    { label: "Previous Month", value: "last_month" },
    { label: "Previous 6 Months", value: "last_6_months" },
    { label: "Current Year", value: "this_year" },
    { label: "Previous Year", value: "last_year" },
    { label: "Current Financial Year", value: "current_financial_year" },
    { label: "FY-Q1 (Apr - Jun)", value: "q1" },
    { label: "FY-Q2 (Jul - Sep)", value: "q2" },
    { label: "FY-Q3 (Oct - Dec)", value: "q3" },
    { label: "FY-Q4 (Jan - Mar)", value: "q4" },
    { label: "Custom Range", value: "custom" },
  ];

  const handleRangeChange = (range: string) => {
    setIsManualCustomChange(false); // Reset

    const dateRange = calculateDateRange(range);

    if (range !== "custom" && dateRange) {
      const start = new Date(dateRange.startDate).toISOString().slice(0, 10);
      const end = new Date(dateRange.endDate).toISOString().slice(0, 10);
      setCustomStartDate(start);
      setCustomEndDate(end);
    } else if (range === "custom") {
      // Let user manually input custom values
      setIsManualCustomChange(true);
    } else {
      setCustomStartDate("");
      setCustomEndDate("");
    }

    // Update filter manually here (don't rely on useEffect)
    if (serverSide && onServerDateRangeChange && dateField && dateRange) {
      onServerDateRangeChange(
        dateField,
        range,
        dateRange.startDate,
        dateRange.endDate
      );
    } else if (dateRange) {
      onDateRangeChange(range, dateRange.startDate, dateRange.endDate);
    } else if (range === "all") {
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      serverSide
        ? onServerDateRangeChange?.("", range)
        : onDateRangeChange(range);
    }
  };

  useEffect(() => {
    if (
      isManualCustomChange &&
      customStartDate &&
      customEndDate &&
      new Date(customStartDate) <= new Date(customEndDate)
    ) {
      const startISO = new Date(customStartDate).toISOString();
      const end = new Date(customEndDate);
      end.setHours(23, 59, 59, 999);
      const endISO = toUTCISOString(end);

      console.log("custom start date", startISO);
      console.log("custom end date:", endISO);

      if (serverSide && onServerDateRangeChange && dateField) {
        onServerDateRangeChange(dateField, "custom", startISO, endISO);
      } else {
        onDateRangeChange("custom", startISO, endISO);
      }
    }
  }, [customStartDate, customEndDate, serverSide, isManualCustomChange]);

  return (
    <div className="flex gap-2">
      <Select
        value={isManualCustomChange ? "custom" : currentRange}
        onValueChange={(value) => handleRangeChange(value)}
      >
        <SelectTrigger
          className={`w-[150px] rounded-full ${theme.rowEvenBg} ${theme.rowText} ${theme.buttonSecondaryBorder} ${theme.buttonSecondaryHover}`}
        >
          <SelectValue placeholder="Date Range" />
        </SelectTrigger>
        <SelectContent className={`rounded-[1rem] ${theme.rowEvenBg}`}>
          {dateRangeOptions.map((option) => (
            <SelectItem
              key={option.value}
              value={option.value}
              className={`rounded-[1rem] ${theme.rowText} ${theme.buttonSecondaryHover}`}
            >
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <div
        className={`flex items-center gap-2 ${
          !enableCustomDateRangeFilter ? "invisible" : ""
        }`}
      >
        <input
          type="date"
          value={customStartDate}
          onChange={(e) => {
            setIsManualCustomChange(true);
            setCustomStartDate(e.target.value);
          }}
          className="rounded-full border px-2 py-1 text-sm"
        />
        <span className="text-sm font-medium">To</span>
        <input
          type="date"
          value={customEndDate}
          onChange={(e) => {
            setIsManualCustomChange(true);
            setCustomEndDate(e.target.value);
          }}
          className="rounded-full border px-2 py-1 text-sm"
        />
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                className={`rounded-full p-2 cursor-pointer hover:bg-gray-200 transition-colors`}
                onClick={() => {
                  setCustomStartDate("");
                  setCustomEndDate("");
                }}
              >
                <RotateCcw className="h-4 w-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent
              className={`${theme.buttonPrimaryBg} ${theme.buttonPrimaryText}`}
            >
              <p>Clear Date Range</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    </div>
  );
};
